import { Link, useRouterState } from '@tanstack/react-router';
import {
  LayoutDashboard, Users, Building2, Activity, Kanban, Download, Settings, UserCog, MapPin, Tag, Trash2, Upload
} from 'lucide-react';
import { cn } from '../../lib/cn.js';
import { useAuth } from '../../lib/auth.js';

interface NavItem {
  to: string;
  label: string;
  icon: React.ReactNode;
  exact?: boolean;
}

function NavLink({ item, onClick }: { item: NavItem; onClick?: () => void }) {
  const router = useRouterState();
  const current = router.location.pathname;
  const isActive = item.exact ? current === item.to : current.startsWith(item.to);

  return (
    <Link
      to={item.to}
      onClick={onClick}
      className={cn(
        'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors min-h-[44px]',
        isActive
          ? 'bg-primary/10 text-primary'
          : 'text-secondary hover:bg-hover-bg hover:text-primary-text'
      )}
    >
      <span className="flex-shrink-0">{item.icon}</span>
      <span>{item.label}</span>
    </Link>
  );
}

interface SidebarProps {
  mobile?: boolean;
  onClose?: () => void;
}

export function Sidebar({ mobile, onClose }: SidebarProps) {
  const { hasRole } = useAuth();

  const mainNav: NavItem[] = [
    { to: '/', label: 'Dashboard', icon: <LayoutDashboard className="w-5 h-5" />, exact: true },
    { to: '/landlords', label: 'Landlords', icon: <Building2 className="w-5 h-5" /> },
    { to: '/activity', label: 'Activity', icon: <Activity className="w-5 h-5" /> },
    { to: '/kanban', label: 'Kanban', icon: <Kanban className="w-5 h-5" /> },
  ];

  const managerNav: NavItem[] = [];
  if (hasRole('manager')) {
    managerNav.push({
      to: '/exports',
      label: 'Export CSV',
      icon: <Download className="w-5 h-5" />,
    });
  }

  const adminNav: NavItem[] = [];
  if (hasRole('manager')) {
    adminNav.push(
      { to: '/admin/junk-review', label: 'Junk Review', icon: <Trash2 className="w-5 h-5" /> },
      { to: '/admin/imports', label: 'Imports', icon: <Upload className="w-5 h-5" /> },
    );
  }
  if (hasRole('admin')) {
    adminNav.push(
      { to: '/admin/users', label: 'Users', icon: <Users className="w-5 h-5" /> },
      { to: '/admin/assignments', label: 'Assignments', icon: <UserCog className="w-5 h-5" /> },
      { to: '/admin/managers', label: 'Managers', icon: <MapPin className="w-5 h-5" /> },
      { to: '/admin/tags', label: 'Tags', icon: <Tag className="w-5 h-5" /> },
    );
  }

  return (
    <nav
      className={cn(
        'flex flex-col h-full py-4 px-3 gap-1',
        mobile && 'pt-0'
      )}
    >
      <div className="space-y-0.5">
        {mainNav.map((item) => (
          <NavLink key={item.to} item={item} onClick={onClose} />
        ))}
      </div>

      {managerNav.length > 0 && (
        <div className="space-y-0.5 mt-2">
          <p className="px-3 py-1 text-xs text-secondary uppercase tracking-wide font-medium">
            Manager
          </p>
          {managerNav.map((item) => (
            <NavLink key={item.to} item={item} onClick={onClose} />
          ))}
        </div>
      )}

      {adminNav.length > 0 && (
        <div className="space-y-0.5 mt-2">
          <p className="px-3 py-1 text-xs text-secondary uppercase tracking-wide font-medium">
            Admin
          </p>
          {adminNav.map((item) => (
            <NavLink key={item.to} item={item} onClick={onClose} />
          ))}
        </div>
      )}
    </nav>
  );
}
